// External Dependencies
import {
  alpha,
  createTheme,
  lighten,
  type ThemeOptions,
} from '@mui/material/styles';
import {
  common,
  deepPurple,
  green,
  purple,
  red,
} from '@mui/material/colors';
import { buttonClasses } from '@mui/material/Button';

// Local Variables
// Borrowed from the many variations in the Stripe web site
// Carried over from the web app
const stripeBlue300 = '#7D96F1';
const stripeBlue400 = '#6974DE';
const stripeBlue500 = '#5469D4';
const stripeBlue600 = '#2F3D89';
const stripeBlue700 = '#2d2d54';

const defaultTheme = createTheme();

/* eslint-disable sort-keys */

// Presto Groove design system colors
const groove = {
  // The snare is sharp, bright, and cuts through the mix —
  //  just like these blues are crisp and prominent in our UI.
  blue: { // Snare
    lightest: '#7D96F1',
    light: '#6974DE',
    main: '#5469D4',
    dark: '#2F3D89',
    darkest: '#2d2d54',
  },
  // The kick drum is the foundation, the heartbeat of any groove.
  // Purple is the primary/foundational color of our brand.
  purple: { // Kick
    lightest: '#F3E9FC',
    light: '#6102CC',
    main: '#450291',
    dark: '#280154',
    darkest: '#1A0033',
  },
};
/* eslint-enable sort-keys */

// Taken from MUI source code: OutlinedInput.js
const defaultBorder = 'rgba(0, 0, 0, 0.23)';

const hoverBgColor = groove.purple.lightest;

// Stock colors
// Nice color names courtesy of https://coolors.co
const languidLavender = '#e0d6eb';

// Theme Definition
const themeOptions: ThemeOptions = {
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          // All button overrides
          '&:hover': {
            boxShadow: 'none',
          },

          boxShadow: 'none',

          // Text button overrides
          [`&.${buttonClasses.text}.${buttonClasses.colorPrimary}`]: {
            '&:hover': {
              backgroundColor: hoverBgColor,
            },
            color: groove.purple.main,
          },
          [`&.${buttonClasses.text}.${buttonClasses.colorError}`]: {
            '&:hover': {
              backgroundColor: alpha(red[200], 0.2),
            },
            color: red[700],
            [`&.${buttonClasses.disabled}`]: {
              color: defaultTheme.palette.text.disabled,
            },
          },

          // Outlined button overrides
          [`&.${buttonClasses.outlined}.${buttonClasses.colorPrimary}`]: {
            '&:hover': {
              backgroundColor: hoverBgColor,
            },
            backgroundColor: common.white,
            border: `1px solid ${defaultBorder}`,
            color: groove.purple.main,
            [`&.${buttonClasses.disabled}`]: {
              color: defaultTheme.palette.text.disabled,
            },
          },
          [`&.${buttonClasses.outlined}.${buttonClasses.colorSecondary}`]: {
            '&:hover': {
              backgroundColor: hoverBgColor,
              border: `1px solid ${groove.blue.light}`,
            },
            backgroundColor: common.white,
            border: `1px solid ${groove.blue.light}`,
            color: groove.blue.light,
            [`&.${buttonClasses.disabled}`]: {
              color: defaultTheme.palette.text.disabled,
            },
          },
          [`&.${buttonClasses.outlined}.${buttonClasses.colorError}`]: {
            '&:hover': {
              backgroundColor: alpha(red[200], 0.2),
            },
            backgroundColor: common.white,
            border: `1px solid ${red[200]}`,
            color: red[700],
            [`&.${buttonClasses.disabled}`]: {
              color: defaultTheme.palette.text.disabled,
            },
          },

          // Contained button overrides
          [`&.${buttonClasses.contained}.${buttonClasses.colorPrimary}`]: {
            '&:disabled': {
              backgroundColor: defaultTheme.palette.action.disabledBackground,
              color: defaultTheme.palette.action.disabled,
            },
            '&:hover': {
              backgroundColor: groove.purple.dark,
            },
            backgroundColor: groove.purple.main,
            color: common.white,
          },
          [`&.${buttonClasses.contained}.${buttonClasses.colorSecondary}`]: {
            '&:disabled': {
              backgroundColor: defaultTheme.palette.action.disabledBackground,
              color: defaultTheme.palette.action.disabled,
            },
            '&:hover': {
              backgroundColor: defaultTheme.palette.primary.dark,
            },
            backgroundColor: defaultTheme.palette.primary.main,
            color: defaultTheme.palette.primary.contrastText,
          },
        },
      },
    },
    MuiButtonGroup: {
      styleOverrides: {
        root: {
          borderRadius: 6,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            // The MUI v6.x component always creates `--IconButton-hoverBg` CSS variable,
            //  but it's undefined when the component is hovered in the DOM.
            // https://github.com/mui/material-ui/blob/v6.4.10/packages/mui-material/src/IconButton/IconButton.js
            // Apply the same hover background color as the source code.
            backgroundColor: alpha(
              defaultTheme.palette.action.active,
              defaultTheme.palette.action.hoverOpacity,
            ),
          },
        },
      },
    },
  },
  palette: {
    accentBlue: '#7795F8',
    dangerZone: red['200'],
    dropzoneBorder: green[200],
    dropzoneHover: lighten(green[50], 0.6),
    feedbackDetails: lighten(purple['50'], 0.4),
    link: languidLavender,
    navMenuItemText: green['900'],
    prestoPrimaryDark: '#280154',
    prestoPrimaryLight: '#6102CC',
    prestoPrimaryMain: '#450291',
    primary: deepPurple,
    secondary: green,
    stripeBlue: {
      300: stripeBlue300,
      400: stripeBlue400,
      500: stripeBlue500,
      600: stripeBlue600,
      700: stripeBlue700,
    },
  },
  shape: {
    // Override default MUI border radius of 4
    borderRadius: 6,
  },
};

export default createTheme({
  ...themeOptions,
  // Fill the `theme.vars` with CSS variables
  // Leave disabled for now, but this will help with dark/light mode in the future
  cssVariables: false,
});
